import { Card } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { MachinePerformancePlot } from 'components/common/modelling/machine-performance-plot';
import { ModelPerformancePlot } from 'components/common/modelling/model-performance-plot';
import { FeatureDemoDiamondView } from 'components/sections/feature-demo/charts/diamond-view';
import {
  DEMO_HITS_PERIOD,
  buildDemoSessionHits,
} from 'components/sections/feature-demo/charts/hitter-profile-fixtures';
import { DEMO_MACHINE_METRICS } from 'components/sections/feature-demo/charts/machine-performance-fixtures';
import { FeatureDemoSessionRecap } from 'components/sections/feature-demo/charts/session-recap';
import { SubSectionBox } from 'components/sections/feature-demo/sub-section-box';
import { HPSummaryHitQuality } from 'components/sections/hitter-library/profile/summary/sections/hit-quality';
import { HPSummaryTotalBP } from 'components/sections/hitter-library/profile/summary/sections/total-bp';
import { HitterProfileSummaryTotalPitchTypes } from 'components/sections/hitter-library/profile/summary/sections/total-pitch-types';
import useHitterProfileStore from 'components/sections/hitter-library/profile/use-hitter-profile-store';
import { useEffect, useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from 'recharts';

const AREA_CHART_DATA = [
  { month: 'January', desktop: 186 },
  { month: 'February', desktop: 305 },
  { month: 'March', desktop: 237 },
  { month: 'April', desktop: 73 },
  { month: 'May', desktop: 209 },
  { month: 'June', desktop: 214 },
];

/**
 * The hitter profile charts take no props and read their data from an app-wide
 * store, so they have to be fed by seeding that store rather than by fixtures
 * passed down as props.
 */
const HitterProfileCharts = () => {
  const demoHits = useMemo(() => buildDemoSessionHits(), []);

  useEffect(() => {
    const { loading, hits, hitsFilter, currSummaryHits, prevSummaryHits } =
      useHitterProfileStore.getState();

    useHitterProfileStore.setState({
      loading: false,
      hits: demoHits,
      hitsFilter: {
        hitterID: 'demo-hitter',
        period: DEMO_HITS_PERIOD,
      },
      currSummaryHits: demoHits,
      prevSummaryHits: [],
    });

    // put the store back so the real hitter library isn't left with demo hits
    return () =>
      useHitterProfileStore.setState({
        loading,
        hits,
        hitsFilter,
        currSummaryHits,
        prevSummaryHits,
      });
  }, [demoHits]);

  return (
    <>
      <SubSectionBox
        title="Area Chart - Gradient Fill With Point Labels"
        source="sections/hitter-library/profile/summary/sections/total-bp"
      >
        <HPSummaryTotalBP />
      </SubSectionBox>

      <SubSectionBox
        title="Bar Chart - Rounded With Value Labels"
        source="sections/hitter-library/profile/summary/sections/total-pitch-types"
      >
        <HitterProfileSummaryTotalPitchTypes />
      </SubSectionBox>

      <SubSectionBox
        title="Pie Chart - Donut With Centre Value And Hover Legend"
        source="sections/hitter-library/profile/summary/sections/hit-quality"
      >
        <HPSummaryHitQuality />
      </SubSectionBox>
    </>
  );
};

export const ChartsDemo = () => {
  return (
    <ErrorBoundary identifier="ChartsDemo">
      <SubSectionBox title="Area Chart - Linear" source="recharts/AreaChart">
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart
            data={AREA_CHART_DATA}
            margin={{ left: 12, right: 12, top: 12, bottom: 12 }}
          >
            <CartesianGrid
              vertical={false}
              stroke="var(--gray-6)"
              strokeOpacity={0.4}
            />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => String(value).slice(0, 3)}
            />
            <Tooltip
              cursor={{ fill: 'var(--accent-9)', fillOpacity: 0.08 }}
              contentStyle={{
                backgroundColor: 'var(--gray-6)',
                border: '1px solid var(--gray-7)',
                color: 'var(--gray-12)',
              }}
              labelStyle={{ color: 'var(--gray-12)' }}
              itemStyle={{ color: 'var(--gray-12)' }}
            />
            <Area
              dataKey="desktop"
              type="linear"
              fill="var(--accent-9)"
              fillOpacity={0.25}
              stroke="var(--accent-11)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </SubSectionBox>

      <SubSectionBox title="Diamond View" source="common/diamond-view">
        <FeatureDemoDiamondView />
      </SubSectionBox>

      <SubSectionBox
        title="Session Recap — Field and Strike Zone"
        source="analytics/session-hits/recap/visualizations"
      >
        <FeatureDemoSessionRecap />
      </SubSectionBox>

      <SubSectionBox
        title="Scatter Chart - Time Series With Connecting Line"
        source="common/modelling/machine-performance-plot"
      >
        <Card className="no-background" style={{ padding: 0 }}>
          <MachinePerformancePlot metrics={DEMO_MACHINE_METRICS} />
        </Card>
      </SubSectionBox>

      <SubSectionBox
        title="Scatter Chart - Time Series, Single Series"
        source="common/modelling/model-performance-plot"
      >
        <Card className="no-background" style={{ padding: 0 }}>
          <ModelPerformancePlot metrics={DEMO_MACHINE_METRICS} />
        </Card>
      </SubSectionBox>

      <HitterProfileCharts />
    </ErrorBoundary>
  );
};
